/**
 * PDP Selling Plan Component
 * Handles subscription vs one-time purchase selection and quantity breaks interaction
 */
export default class PDPSellingPlan extends HTMLElement {
  connectedCallback() {
    this.setupElements();
    this.bindEvents();
    this.initializeState();
  }

  setupElements() {
    // Cache DOM elements
    this.oneTimeRadios = this.querySelectorAll('.one_time_purchase');
    this.subscriptionRadios = this.querySelectorAll('.subscription__plan');
    this.sellingPlanRadios = this.querySelectorAll('input[name="selling_plan"]');
    this.sellingPlanOptions = this.querySelector('.js-selling-plan-options');
    this.sellingPlanSelect = this.querySelector('select[name="selling_plan"]');
    
    // Get product variant ID from data attribute or first available variant
    this.productVariantId = this.dataset.productVariantId;
  }

  bindEvents() {
    // Bind one-time purchase radio events
    this.oneTimeRadios.forEach(radio => {
      radio.addEventListener('change', this.handleOneTimeSelection.bind(this));
    });

    // Bind subscription radio events
    this.subscriptionRadios.forEach(radio => {
      radio.addEventListener('change', this.handleSubscriptionSelection.bind(this));
    });

    // Bind selling plan option changes
    this.sellingPlanRadios.forEach(radio => {
      radio.addEventListener('change', this.handleSellingPlanOptionChange.bind(this));
    });

    // Listen for quantity break changes to reset to one-time purchase
    document.addEventListener('quantity-break-changed', this.handleQuantityBreakChange.bind(this));

    // Listen for variant picker changes to update product variant
    this.addEventListener('variant-picker-changed', this.handleVariantPickerChange.bind(this));
  }

  initializeState() {
    // Set initial state - one-time purchase should be selected by default
    const checkedOneTime = this.querySelector('.one_time_purchase:checked');
    if (checkedOneTime) {
      this.handleOneTimeSelection({ target: checkedOneTime });
    }
  }

  disableSellingPlanSelect() {
    if (this.sellingPlanSelect) {
      this.sellingPlanSelect.disabled = true;
    }
  }
  
  enableSellingPlanSelect() {
    if (this.sellingPlanSelect) {
      this.sellingPlanSelect.disabled = false;
  
      // If nothing selected, select first option
      if (!this.sellingPlanSelect.value) {
        this.sellingPlanSelect.selectedIndex = 0;
      }
    }
  } 
  

  handleOneTimeSelection(event) {
    console.log('One-time purchase selected');
    this.disableSellingPlanSelect();
    // Clear selling plan selections
    this.clearSellingPlanSelections();
    
    // Collapse selling plan options
    this.collapseSellingPlanOptions();
    
    // Dispatch event to notify other components
    this.dispatchSellingPlanChange({
      isOneTime: true,
      isSubscription: false,
      sellingPlanId: null
    });
    
    // Ensure form uses variant picker selection
    this.maintainVariantPickerSelection();
  }

  handleSubscriptionSelection(event) {
    console.log('Subscription selected');
    this.enableSellingPlanSelect();
    // Select first selling plan option
    this.selectFirstSellingPlan();
    
    // Expand selling plan options
    this.expandSellingPlanOptions();
    
    // Dispatch event to notify other components
    const selectedSellingPlan = this.querySelector('input[name="selling_plan"]:checked');
    this.dispatchSellingPlanChange({
      isOneTime: false,
      isSubscription: true,
      sellingPlanId: selectedSellingPlan ? selectedSellingPlan.value : null
    });
    
    // Ensure form uses variant picker selection
    this.maintainVariantPickerSelection();
  }

  handleSellingPlanOptionChange(event) {
    const selectedSellingPlan = event.target;
    
    // Dispatch event when selling plan option changes
    this.dispatchSellingPlanChange({
      isOneTime: false,
      isSubscription: true,
      sellingPlanId: selectedSellingPlan.value
    });
    
    // Ensure form uses variant picker selection
    this.maintainVariantPickerSelection();
  }

  dispatchSellingPlanChange(selectionState) {
    // Always get the current variant from the variant picker instead of using stored productVariantId
    const currentFormVariantId = this.getCurrentVariantFromForm();
    
    // Dispatch custom event for other components to listen to
    const event = new CustomEvent('selling-plan-changed', {
      detail: {
        ...selectionState,
        productVariantId: currentFormVariantId || this.productVariantId
      },
      bubbles: true
    });
    
    console.log(`Selling plan changed - using variant ID: ${currentFormVariantId || this.productVariantId}`);
    document.dispatchEvent(event);
  }

  /**
   * Maintain variant picker selection in the form
   * This ensures selling plan changes don't override variant picker selections
   */
  maintainVariantPickerSelection() {
    const productForms = document.querySelectorAll('form[action*="/cart/add"], .main-product-form');
    
    productForms.forEach(form => {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput) {
        const wasSetByVariantPicker = variantInput.getAttribute('data-variant-picker-set') === 'true';
        
        if (wasSetByVariantPicker) {
          const variantPickerValue = variantInput.value;
          console.log(`Selling plan: Maintaining variant picker selection: ${variantPickerValue}`);
          
          // Ensure the value hasn't been changed by other components
          setTimeout(() => {
            if (variantInput.value !== variantPickerValue) {
              console.log(`Selling plan: Restoring variant picker value from ${variantInput.value} to ${variantPickerValue}`);
              variantInput.value = variantPickerValue;
              variantInput.setAttribute('data-variant-picker-set', 'true');
              variantInput.setAttribute('data-variant-picker-timestamp', Date.now().toString());
            }
          }, 10);
        }
      }
    });
  }

  /**
   * Get the current variant ID from the form (set by variant picker)
   * @returns {string|null} - The current variant ID from the form
   */
  getCurrentVariantFromForm() {
    // Find the main product form
    const productForms = document.querySelectorAll('form[action*="/cart/add"], .main-product-form');
    
    for (const form of productForms) {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput) {
        // Prefer variant picker selections over other sources
        const wasSetByVariantPicker = variantInput.getAttribute('data-variant-picker-set') === 'true';
        if (wasSetByVariantPicker) {
          console.log(`Using variant picker selection: ${variantInput.value}`);
          return variantInput.value;
        }
        
        // Fallback to current form value
        return variantInput.value;
      }
    }
    
    return null;
  }

  clearSellingPlanSelections() {
    this.sellingPlanRadios.forEach(radio => {
      radio.checked = false;
    });
  }

  selectFirstSellingPlan() {
    if (this.sellingPlanRadios.length > 0) {
      this.sellingPlanRadios[0].checked = true;
    }
  }

  collapseSellingPlanOptions() {
    if (this.sellingPlanOptions) {
      this.sellingPlanOptions.style.height = '0';
      this.sellingPlanOptions.classList.remove('active');
    }
  }

  expandSellingPlanOptions() {
    if (this.sellingPlanOptions) {
      const scrollHeight = this.sellingPlanOptions.scrollHeight;
      this.sellingPlanOptions.style.height = `${scrollHeight}px`;
      this.sellingPlanOptions.classList.add('active');
    }
  }


  // Public method to get current selection state
  getSelectionState() {
    const oneTimeSelected = this.querySelector('.one_time_purchase:checked');
    const subscriptionSelected = this.querySelector('.subscription__plan:checked');
    const selectedSellingPlan = this.querySelector('input[name="selling_plan"]:checked');

    // Always use the current form variant (from variant picker) instead of stored productVariantId
    const currentFormVariantId = this.getCurrentVariantFromForm();

    return {
      isOneTime: !!oneTimeSelected,
      isSubscription: !!subscriptionSelected,
      sellingPlanId: selectedSellingPlan ? selectedSellingPlan.value : null,
      productVariantId: currentFormVariantId || this.productVariantId
    };
  }

  // Handle quantity break changes
  handleQuantityBreakChange(event) {
    const { variantId, parentProductId } = event.detail;
    
    // Only reset if we're currently in subscription mode
    const subscriptionSelected = this.querySelector('.subscription__plan:checked');
    if (subscriptionSelected) {
      this.resetToOneTimePurchase();
    }
    
    // Update the product variant ID
    this.updateProductVariant(variantId);
  }

  // Handle variant picker changes
  handleVariantPickerChange(event) {
    const { variantId, variant } = event.detail;
    
    console.log(`Selling plan received variant picker change: ${variantId}`);
    
    // Update the product variant ID to use the selected variant from variant picker
    this.updateProductVariant(variantId);
    
    // Re-dispatch selling plan change event with updated variant
    const currentState = this.getSelectionState();
    this.dispatchSellingPlanChange(currentState);
    
    // Ensure the form maintains the variant picker selection
    this.ensureVariantPickerPriority(variantId);
  }

  /**
   * Ensure that variant picker selection is maintained in the form
   * @param {string} variantId - The variant ID from variant picker
   */
  ensureVariantPickerPriority(variantId) {
    // Find the main product form and ensure it uses the variant picker selection
    const productForms = document.querySelectorAll('form[action*="/cart/add"], .main-product-form');
    
    productForms.forEach(form => {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput) {
        const currentValue = variantInput.value;
        
        // If the form value doesn't match the variant picker selection, restore it
        if (currentValue !== variantId) {
          console.log(`Selling plan: Restoring variant picker selection from ${currentValue} to ${variantId}`);
          variantInput.value = variantId;
          variantInput.setAttribute('data-variant-picker-set', 'true');
          variantInput.setAttribute('data-variant-picker-timestamp', Date.now().toString());
        }
      }
    });
  }

  // Reset selling plan to one-time purchase
  resetToOneTimePurchase() {
    // Uncheck all subscription radios
    this.subscriptionRadios.forEach(radio => {
      radio.checked = false;
    });

    // Check the first one-time purchase radio
    if (this.oneTimeRadios.length > 0) {
      this.oneTimeRadios[0].checked = true;
    }

    // Clear all selling plan selections
    this.clearSellingPlanSelections();

    // Collapse selling plan options
    this.collapseSellingPlanOptions();

    // Dispatch event to notify other components
    this.dispatchSellingPlanChange({
      isOneTime: true,
      isSubscription: false,
      sellingPlanId: null
    });
  }

  // Public method to update product variant ID
  updateProductVariant(variantId) {
    this.productVariantId = variantId;
    this.dataset.productVariantId = variantId;
  }

  // Clean up event listeners when component is removed
  disconnectedCallback() {
    document.removeEventListener('quantity-break-changed', this.handleQuantityBreakChange.bind(this));
    this.removeEventListener('variant-picker-changed', this.handleVariantPickerChange.bind(this));
  }
}
